import {
  Button,
  ChakraBox,
  ChakraFlex,
  ChakraHStack,
  ChakraSpacer,
  ChakraTag,
  ChakraTagCloseButton,
  ChakraTagLabel,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  Reset,
} from '@equip.health/ui';

import { UNASSIGNED_PATIENT_OPTION } from '~/lib/constants/user.constants';

const { labelBold } = EquipUITheme.typography;
const { primary } = EquipUIFireflyV2Theme.colors;

type OptionWithKey = AutoCompleteOption & { keyName: string };

const getTagLabel = ({
  keyName,
  primaryText,
  value,
}: OptionWithKey): string => {
  if (keyName === 'patient' && value === UNASSIGNED_PATIENT_OPTION.value)
    return UNASSIGNED_PATIENT_OPTION.primaryText;
  if (keyName === 'creator') return `Created by ${primaryText}`;
  if (keyName === 'title') return `"${primaryText}"`;
  return primaryText;
};

type TaskFilterActionBarProps = {
  filters: TTaskFilters;
  handleRemove: (keyName: string, option: AutoCompleteOption) => void;
  handleReset: () => void;
};

const TAG_HEIGHT = 25;
const TAG_VERTICAL_MARGIN = 4;
// compute space for two rows of filters
export const FILTER_ACTION_BAR_HEIGHT =
  2 * (TAG_HEIGHT + 2 * TAG_VERTICAL_MARGIN);

const TOP_LEVEL_ID = 'task-filter-action-bar';

const TaskFilterActionBar = ({
  filters,
  handleRemove,
  handleReset,
}: TaskFilterActionBarProps) => (
  <ChakraFlex alignItems="center" marginTop="24px">
    <ChakraBox {...labelBold}>Filters</ChakraBox>
    <ChakraSpacer />
    <ChakraHStack
      alignItems="center"
      display="flex"
      flexWrap="wrap"
      spacing={4}
    >
      {Object.keys(filters)
        .flatMap(
          (keyName: string): OptionWithKey =>
            filters[keyName].value.map((fValue: AutoCompleteOption) => ({
              ...fValue,
              keyName,
            })),
        )
        .map((option: OptionWithKey) => {
          const { keyName, value } = option;
          const id = `${keyName}::tag::${value}`;
          return (
            <ChakraTag
              bgColor="secondary.50"
              borderRadius="8px"
              color="white"
              data-testid={id}
              height={`${TAG_HEIGHT}px`}
              id={`${TOP_LEVEL_ID}__${keyName}-${value}`}
              key={id}
              margin={`${TAG_VERTICAL_MARGIN}px 0 ${TAG_VERTICAL_MARGIN}px 8px !important`}
              size="md"
              variant="solid"
            >
              <ChakraTagLabel {...labelBold} data-testid={`taglabel::${value}`}>
                {getTagLabel(option)}
              </ChakraTagLabel>
              <ChakraTagCloseButton
                fontSize="24px"
                onClick={() => handleRemove(keyName, option)}
              />
            </ChakraTag>
          );
        })}
    </ChakraHStack>
    <Button
      {...labelBold}
      id={`${TOP_LEVEL_ID}__reset-filters-2`}
      key="reset"
      margin="0 0 0 8px"
      onClick={handleReset}
      rightIcon={<Reset fill={primary[100]} height="20px" width="20px" />}
      size="sm"
      variant="ghost"
    >
      Reset filters
    </Button>
  </ChakraFlex>
);

export default TaskFilterActionBar;
